import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { fontWeights } from '../../../theme/type';
import { colors } from '../../../theme/colors';
import { media } from '../../../theme/media';
import { rem } from '../../../utilities/style';

import TextRenderer from '../../TextRenderer';
import Select from '../../Select';

const MobileDropdownContainer = styled.div`
  margin: 0 auto ${rem(40)};
  max-width: 450px;

  ${media('>small')} {
    display: none;
  }
`;

const MobileDropdownLabel = styled.div`
  color: ${colors.blue['800']};
  font-weight: ${fontWeights.semiBold};
  margin-bottom: 8px;
`;

const MobileDropdown = styled(Select)`
  display: block;
`;

const Container = styled.div`
  margin: 0 ${rem(-20)} ${rem(40)};
  text-align: center;
  overflow-x: auto;
  overflow-y: hidden;
  background-image: linear-gradient(${colors.gray['300']} , ${colors.gray['300']});
  background-size: calc(100% - ${rem(40)}) 1px;
  background-position: left ${rem(20)} bottom;
  background-repeat: no-repeat;
  background-attachment: local;

  ${({ hideSmall }) => hideSmall && `
    ${media('<=small')} {
      display: none;
    }
  `}
`;

const Buttons = styled.div`
  display: inline-flex;
  gap: ${rem(24)};
  margin-bottom: ${rem(30)};
  padding: 0 ${rem(20)};
`;

const Button = styled.button`
  color: ${colors.gray['600']};
  padding: ${rem(6)} ${rem(16)};
  border: 1px solid ${colors.gray['600']};
  border-radius: ${rem(50)};
  background-color: ${colors.white};
  white-space: nowrap;
  cursor: pointer;
  transition: all 200ms ease;

  &:hover {
    color: ${colors.white};
    background-color: ${colors.blue['500']};
    border: 1px solid ${colors.blue['500']};
  }

  ${({ active }) => active && `
    color: ${colors.white};
    background-color: ${colors.blue['500']};
    border: 1px solid ${colors.blue['500']};
  `}
`;

const TabMenu = ({
  tabNames,
  activeIndex,
  onChange,
  ariaControlIds = [],
  showMobileDropdown,
  mobileDropdownLabel
}) => {
  const tabRefs = useRef(tabNames.map(() => React.createRef()));

  const switchTabOnArrowPress = useCallback(({ keyCode, target }) => {
    const currentIndex = tabRefs.current.findIndex((tab) => (
      tab.current.getAttribute('aria-controls') === target.getAttribute('aria-controls')
    ));
    // left
    if (keyCode === 37) {
      if (currentIndex > 0) {
        tabRefs.current[currentIndex - 1].current.focus();
      }
    }
    // right
    if (keyCode === 39) {
      if (currentIndex < tabRefs.current.length - 1) {
        tabRefs.current[currentIndex + 1].current.focus();
      }
    }
  }, [tabRefs]);

  useEffect(() => {
    tabRefs.current.forEach((tab) => {
      tab.current.addEventListener('keydown', (event) => switchTabOnArrowPress(event));
    });
  }, [switchTabOnArrowPress]);

  return (
    <>
      {showMobileDropdown && (
        <MobileDropdownContainer>
          {mobileDropdownLabel && (
            <MobileDropdownLabel>
              <TextRenderer text={mobileDropdownLabel} />
            </MobileDropdownLabel>
          )}
          <MobileDropdown
            options={tabNames.map((tabName, index) => ({
              value: index,
              label: tabName
            }))}
            defaultValue={activeIndex}
            onChange={onChange}
            fullWidth
          />
        </MobileDropdownContainer>
      )}
      <Container role="tablist" hideSmall={showMobileDropdown}>
        <Buttons>
          {tabNames.map((tabName, index) => (
            <Button
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              active={index === activeIndex}
              onClick={() => onChange(index)}
              role="tab"
              aria-selected={index === activeIndex}
              ref={tabRefs.current[index]}
              aria-controls={ariaControlIds[index]}
              tabIndex={index === activeIndex ? null : '-1'}
            >
              <TextRenderer text={tabName} />
            </Button>
          ))}
        </Buttons>
      </Container>
    </>
  );
};

TabMenu.propTypes = {
  tabNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeIndex: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaControlIds: PropTypes.arrayOf(PropTypes.string),
  showMobileDropdown: PropTypes.bool,
  mobileDropdownLabel: PropTypes.string
};

export default TabMenu;
